(async () => {
  const elementName = 'media-gallery-grid';

  if (customElements.get(elementName)) return;

  /*
   * The grid section will load media-gallery-carousel.js first.
   * Wait for its shared component before extending it.
   */
  await customElements.whenDefined('media-gallery-carousel');

  if (customElements.get(elementName)) return;

  const GalleryBase = customElements.get('media-gallery-carousel');

  class MediaGalleryGrid extends GalleryBase {
    initialize() {
      /*
       * Let the base component set up lazy loading, media controls,
       * popup closing, and cleanup.
       */
      this.dataset.source = 'metaobject_group';
      this.dataset.autoScroll = 'false';
      this.dataset.shuffleGroup = 'false';

      // Each initialization rebuilds the displayed grid page.
      this.dataset.groupReady = 'false';
      this.gridEntries = null;
      this.gridPage = 1;
      this.gridFilter = 'all';

      super.initialize();

      if (!this.track || !this.signal || this.signal.aborted) return;

      this.readGridLocation();

      this.listen(this, 'click', event => {
        const pageButton = event.target.closest('[data-mg-grid-page]');

        if (pageButton && this.contains(pageButton)) {
          event.preventDefault();

          if (pageButton.disabled) return;

          const page = Number(pageButton.dataset.mgGridPage);

          if (!Number.isInteger(page) || !this.gridEntries) return;

          this.gridPage = page;
          this.renderGridPage({
            updateUrl: true,
            moveFocus: true
          });

          return;
        }

        const filterButton = event.target.closest(
          '[data-mg-grid-filter]'
        );

        if (filterButton && this.contains(filterButton)) {
          event.preventDefault();

          if (!this.gridEntries) return;

          this.gridFilter = this.validFilter(
            filterButton.dataset.mgGridFilter
          );

          this.gridPage = 1;

          this.renderGridPage({
            updateUrl: true,
            moveFocus: false
          });
        }
      });

      this.listen(window, 'popstate', () => {
        this.readGridLocation();

        if (this.gridEntries) {
          this.renderGridPage({
            updateUrl: false,
            moveFocus: false
          });
        }
      });
    }

    // A grid never needs carousel arrows or a scrolling timer.
    get hasOverflow() {
      return false;
    }

    schedule() {
      this.stopTimer();
    }

    advance() {
      // Horizontal carousel navigation is not used by the grid.
    }

    updateNavigation() {
      if (this.prevButton) this.prevButton.hidden = true;
      if (this.nextButton) this.nextButton.hidden = true;

      const autoControls = this.querySelector('[data-mg-auto-controls]');
      if (autoControls) autoControls.hidden = true;
    }

    /*
     * Grid videos are in popups. Do not apply the carousel's horizontal
     * offscreen-media test to a vertical grid.
     */
    pauseOffscreenMedia() {}

    get pageParameter() {
      return this.dataset.pageParameter || 'gallery_page';
    }

    get filterParameter() {
      return this.dataset.filterParameter || 'gallery_type';
    }

    get pageSize() {
      return Math.max(
        1,
        Math.min(48, Number(this.dataset.pageSize) || 12)
      );
    }

    validFilter(value) {
      const allowed = ['all', 'photos', 'videos'];
      return allowed.includes(value) ? value : 'all';
    }

    readGridLocation() {
      const url = new URL(location.href);
      const requestedPage = Number(
        url.searchParams.get(this.pageParameter) || 1
      );

      this.gridPage = Number.isInteger(requestedPage) &&
        requestedPage > 0 ? requestedPage : 1;

      const defaultFilter = this.validFilter(
        this.dataset.defaultFilter || 'all'
      );

      /*
       * A fixed content type can be used for a video-only or photo-only
       * full-gallery page. Optional visitor filters are separate.
       */
      if (this.dataset.showFilters === 'true') {
        this.gridFilter = this.validFilter(
          url.searchParams.get(this.filterParameter) || defaultFilter
        );
      } else {
        this.gridFilter = defaultFilter;
      }
    }

    async loadGroup() {
      if (this.loadingGroup || this.groupReady) return;

      const signal = this.signal;
      const url = new URL(location.href);

      const urlGroup = (
        url.searchParams.get('gallery_group') || ''
      ).trim();

      const editorPreview = this.dataset.designMode === 'true';
      const requireUrlGroup = this.dataset.requireUrlGroup === 'true';
      const configuredGroup = (
        this.dataset.galleryGroup || ''
      ).trim();

      const requestedGroup = requireUrlGroup
        ? urlGroup || (editorPreview ? configuredGroup : '')
        : configuredGroup;

      const group = this.normalize(requestedGroup);

      if (!group) {
        this.showUnavailableGallery();
        return;
      }

      this.loadingGroup = true;
      this.setAttribute('aria-busy', 'true');
      this.showStatus('Loading gallery…');

      try {
        const [entries, pageFiles] = await Promise.all([
          this.fetchCandidates(signal),
          this.fetchPageFiles(signal, group)
        ]);

        if (signal.aborted) return;

        const seen = new Set();

        const candidates = entries.filter(entry => {
          if (this.normalize(entry.group) !== group) return false;
          if (!entry.id || seen.has(entry.id)) return false;

          let usable = false;

          if (entry.media_type === 'image') {
            usable = Boolean(this.safeUrl(entry.image_url));
          } else if (entry.media_type === 'video') {
            usable = Array.isArray(entry.video_sources) &&
              entry.video_sources.some(source =>
                this.safeUrl(source.url)
              );
          } else if (entry.media_type === 'external_video') {
            usable = Boolean(this.safeUrl(entry.external_url));
          }

          if (usable) seen.add(entry.id);
          return usable;
        });

        const featured = [];
        const remaining = [];

        candidates.forEach((entry, index) => {
          const raw = entry.featured_position;

          const position = raw === null || raw === undefined ||
            String(raw).trim() === '' ? null : Number(raw);

          const item = { entry, index, position };

          if (
            Number.isInteger(position) &&
            position >= 1 &&
            position <= 5
          ) {
            featured.push(item);
          } else {
            remaining.push(item);
          }
        });

        featured.sort((a, b) =>
          a.position - b.position || a.index - b.index
        );

        const opening = featured.slice(0, 5);

        const rest = [
          ...featured.slice(5),
          ...remaining
        ].sort((a, b) => a.index - b.index);

        const orderedMetaobjects = [...opening, ...rest].map(
          item => item.entry
        );

        this.gridEntries = [
          ...orderedMetaobjects,
          ...pageFiles
        ];

        this.groupReady = true;
        this.dataset.groupReady = 'true';

        if (!this.gridEntries.length) {
          this.showUnavailableGallery();
          return;
        }

        this.restoreGalleryContent();

        const groupTitle = String(
          this.gridEntries[0].group || requestedGroup
        ).trim();

        const heading = this.querySelector(
          '.media-gallery__heading'
        );

        if (
          heading &&
          this.dataset.useConfiguredHeading !== 'true'
        ) {
          heading.textContent = groupTitle;
        }

        this.renderGridPage({
          updateUrl: false,
          moveFocus: false
        });
      } catch (error) {
        if (signal.aborted) return;

        console.error('Media gallery grid source:', error);
        this.showStatus(
          'The gallery could not load. Please reload to try again.'
        );
      } finally {
        if (!signal.aborted) {
          this.loadingGroup = false;
          this.removeAttribute('aria-busy');
        }
      }
    }

    async fetchPageFiles(signal, group) {
      const requireUrlGroup =
        this.dataset.requireUrlGroup === 'true';

      const currentUrl = new URL(location.href);
      let sourcePage = new URL(currentUrl);

      /*
       * Regular sections read their own page.
       * The shared destination can read the originating page
       * supplied by a gallery link.
       */
      if (requireUrlGroup) {
        const originPage = (
          currentUrl.searchParams.get('gallery_source') || ''
        ).trim();

        if (originPage) {
          const candidate = new URL(originPage, location.origin);

          if (
            candidate.origin !== location.origin ||
            !candidate.pathname.includes('/pages/')
          ) {
            throw new Error('Invalid gallery source page.');
          }

          sourcePage = candidate;
        }
      }

      /*
       * Preserve theme-preview context, but do not carry the
       * destination's alternate template or gallery pagination.
       */
      ['preview_theme_id', '_fd', 'pb'].forEach(parameter => {
        const value = currentUrl.searchParams.get(parameter);

        if (value !== null) {
          sourcePage.searchParams.set(parameter, value);
        }
      });

      sourcePage.hash = '';
      sourcePage.searchParams.delete('view');
      sourcePage.searchParams.delete('sections');
      sourcePage.searchParams.delete('page');
      sourcePage.searchParams.delete('gallery_group');
      sourcePage.searchParams.delete('gallery_source');
      sourcePage.searchParams.delete(this.pageParameter);
      sourcePage.searchParams.delete(this.filterParameter);

      sourcePage.searchParams.set(
        'section_id',
        this.dataset.sourceSection || 'media-gallery-source'
      );

      const base = new URL(sourcePage);
      let url = new URL(base);
      let expectedPage = 1;

      const files = [];
      const seenIds = new Set();
      const visited = new Set();

      while (url) {
        if (visited.has(url.href)) {
          throw new Error('Repeated page-file pagination URL.');
        }

        visited.add(url.href);

        const response = await fetch(url, {
          credentials: 'same-origin',
          signal
        });

        if (!response.ok) {
          throw new Error(
            `Page-file source returned HTTP ${response.status}.`
          );
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const payload = parsed.querySelector(
          '[data-mg-page-files-data]'
        );

        if (!payload) {
          throw new Error('Page-file source data was not found.');
        }

        const data = JSON.parse(payload.textContent);

        if (
          data.available !== true ||
          !Array.isArray(data.entries)
        ) {
          throw new Error('Page-file source is malformed.');
        }

        /*
         * Files belong only to their page's assigned group.
         * Never merge a different page group into this gallery.
         */
        if (this.normalize(data.group) !== group) {
          return [];
        }

        const page = Number(data.page);
        const pages = Number(data.pages);
        const total = Number(data.total_files);

        if (
          !Number.isInteger(page) ||
          !Number.isInteger(pages) ||
          page !== expectedPage ||
          pages < page ||
          !Number.isInteger(total) ||
          total < 0
        ) {
          throw new Error('Unexpected page-file pagination.');
        }

        if (total > 25000 || pages > 500) {
          throw new Error(
            'The page-file list exceeds the retrieval limit.'
          );
        }

        data.entries.forEach(entry => {
          if (!entry.id || seenIds.has(entry.id)) return;
          if (this.normalize(entry.group) !== group) return;

          let usable = false;

          if (entry.media_type === 'image') {
            usable = Boolean(this.safeUrl(entry.image_url));
          } else if (entry.media_type === 'video') {
            usable = Array.isArray(entry.video_sources) &&
              entry.video_sources.some(
                source => this.safeUrl(source.url)
              );
          }

          if (!usable) return;

          seenIds.add(entry.id);
          files.push(entry);
        });

        if (page >= pages) {
          url = null;
          continue;
        }

        if (!data.next_url || !data.page_parameter) {
          throw new Error('Missing page-file pagination link.');
        }

        const next = new URL(data.next_url, base);
        const nextValue = next.searchParams.get(
          data.page_parameter
        );

        if (!nextValue) {
          throw new Error('Missing page-file pagination value.');
        }

        url = new URL(base);
        url.searchParams.set(data.page_parameter, nextValue);
        expectedPage += 1;
      }

      return files;
    }

    showUnavailableGallery() {
      this.stopMedia();

      if (this.track) {
        this.track.replaceChildren();
        this.track.hidden = true;
      }

      this.querySelectorAll('[data-mg-generated-dialog]').forEach(
        dialog => dialog.remove()
      );

      [
        '.media-gallery__header',
        '.media-gallery-grid__filters',
        '[data-mg-grid-pagination]'
      ].forEach(selector => {
        const element = this.querySelector(selector);

        if (element) {
          if (!element.hasAttribute('data-mg-original-hidden')) {
            element.dataset.mgOriginalHidden =
              element.hidden ? 'true' : 'false';
          }

          element.hidden = true;
        }
      });

      this.showStatus(
        this.dataset.unavailableLabel ||
        'Gallery not found. Please open a gallery link from one of our pages.'
      );
    }

    restoreGalleryContent() {
      if (this.track) {
        this.track.hidden = false;
      }

      this.querySelectorAll('[data-mg-original-hidden]').forEach(
        element => {
          element.hidden =
            element.dataset.mgOriginalHidden === 'true';

          element.removeAttribute('data-mg-original-hidden');
        }
      );

      this.showStatus('');
    }

    get filteredEntries() {
      if (!Array.isArray(this.gridEntries)) return [];

      if (this.gridFilter === 'photos') {
        return this.gridEntries.filter(
          entry => entry.media_type === 'image'
        );
      }

      if (this.gridFilter === 'videos') {
        return this.gridEntries.filter(
          entry => entry.media_type === 'video' ||
            entry.media_type === 'external_video'
        );
      }

      return this.gridEntries;
    }

    renderGridPage({ updateUrl = false, moveFocus = false } = {}) {
      if (!Array.isArray(this.gridEntries)) return;

      const entries = this.filteredEntries;
      const totalPages = Math.max(
        1,
        Math.ceil(entries.length / this.pageSize)
      );

      this.gridPage = Math.max(
        1,
        Math.min(totalPages, this.gridPage)
      );

      const start = (this.gridPage - 1) * this.pageSize;
      const currentEntries = entries.slice(start, start + this.pageSize);

      /*
       * The shared renderer removes previous generated dialogs and
       * only creates media cards for this grid page.
       */
      this.photoItems = null;
      this.photoIndex = 0;
      this.renderGroup(currentEntries);

      this.showStatus(
        entries.length
          ? ''
          : this.dataset.emptyLabel || 'No media matches this selection.'
      );

      this.renderPagination(entries.length, totalPages);
      this.updateFilters();

      if (updateUrl) this.writeGridLocation();

      if (moveFocus) {
        this.track.focus({ preventScroll: true });

        this.scrollIntoView({
          behavior: this.motionQuery.matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    }

    updateFilters() {
      this.querySelectorAll('[data-mg-grid-filter]').forEach(button => {
        const selected =
          button.dataset.mgGridFilter === this.gridFilter;

        button.setAttribute(
          'aria-pressed',
          selected ? 'true' : 'false'
        );
      });
    }

    pageButton(page, text, options = {}) {
      const button = this.node(
        'button',
        'media-gallery-grid__page-button'
      );

      button.type = 'button';
      button.dataset.mgGridPage = String(page);
      button.disabled = Boolean(options.disabled);

      if (options.current) {
        button.setAttribute('aria-current', 'page');
      }

      const accessibleLabel = options.label || String(text);
      button.setAttribute('aria-label', accessibleLabel);

      if (options.arrow) {
        button.classList.add(
          'media-gallery-grid__page-button--arrow'
        );

        button.title = accessibleLabel;

        const svg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );

        svg.setAttribute('viewBox', '0 0 256 512');
        svg.setAttribute('width', '10');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');

        const path = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        );

        path.setAttribute('fill', 'currentColor');

        path.setAttribute(
          'd',
          options.arrow === 'previous'
            ? 'M31.7 239l136-136c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9L127.9 256l96.4 96.4c9.4 9.4 9.4 24.6 0 33.9L201.7 409c-9.4 9.4-24.6 9.4-33.9 0l-136-136c-9.5-9.4-9.5-24.6-.1-34z'
            : 'M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z'
        );

        svg.appendChild(path);
        button.appendChild(svg);
      } else {
        button.textContent = String(text).padStart(2, '0');
      }

      return button;
    }

    renderPagination(totalItems, totalPages) {
      const pagination = this.querySelector('[data-mg-grid-pagination]');
      const controls = this.querySelector('[data-mg-grid-page-controls]');
      const summary = this.querySelector('[data-mg-grid-summary]');

      if (!pagination || !controls) return;

      pagination.hidden = totalItems === 0;
      controls.replaceChildren();
      controls.hidden = totalPages <= 1;

      if (summary) {
        summary.textContent = '';
        summary.hidden = true;
      }

      if (totalPages <= 1) return;

      if (this.gridPage > 1) {
        controls.appendChild(this.pageButton(
          this.gridPage - 1,
          this.dataset.previousPageLabel || 'Previous',
          { arrow: 'previous' }
        ));
      }

      const visiblePages = new Set([1, totalPages]);

      for (
        let page = this.gridPage - 2;
        page <= this.gridPage + 2;
        page++
      ) {
        if (page >= 1 && page <= totalPages) {
          visiblePages.add(page);
        }
      }

      const pages = [...visiblePages].sort((a, b) => a - b);
      let previous = 0;

      pages.forEach(page => {
        if (previous && page - previous > 1) {
          const ellipsis = this.node(
            'span',
            'media-gallery-grid__ellipsis',
            '…'
          );

          ellipsis.setAttribute('aria-hidden', 'true');
          controls.appendChild(ellipsis);
        }

        const label = (
          this.dataset.pageLabelTemplate || 'Page {page}'
        ).replaceAll('{page}', String(page));

        controls.appendChild(this.pageButton(
          page,
          String(page),
          {
            current: page === this.gridPage,
            label
          }
        ));

        previous = page;
      });

      if (this.gridPage < totalPages) {
        controls.appendChild(this.pageButton(
          this.gridPage + 1,
          this.dataset.nextPageLabel || 'Next',
          { arrow: 'next' }
        ));
      }
    }

    writeGridLocation() {
      const url = new URL(location.href);

      if (this.gridPage > 1) {
        url.searchParams.set(
          this.pageParameter,
          String(this.gridPage)
        );
      } else {
        url.searchParams.delete(this.pageParameter);
      }

      const defaultFilter = this.validFilter(
        this.dataset.defaultFilter || 'all'
      );

      if (
        this.dataset.showFilters === 'true' &&
        this.gridFilter !== defaultFilter
      ) {
        url.searchParams.set(
          this.filterParameter,
          this.gridFilter
        );
      } else {
        url.searchParams.delete(this.filterParameter);
      }

      if (url.href !== location.href) {
        history.pushState(null, '', url);
      }
    }
  }

  customElements.define(elementName, MediaGalleryGrid);
})();